import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { patchClientOnboardingApplicationsBundle } from "@/hooks/useClientOnboardingApplications";
import { CLIENT_OPERATING_CONTEXTS_QUERY_KEY } from "@/hooks/useClientPlatformAccess";
import { acknowledgeApproval } from "@/services/clientOnboarding";

export type ApprovalAcknowledgementPhase = "pending" | "acked" | "error";

/**
 * Finish the one-time hand-off from approved onboarding into the client
 * console: record the acknowledgement, patch the cache the gate reads, then
 * release navigation. The server acknowledgement and the cache patch must
 * both land before navigating; otherwise `OnboardingGate` sees an
 * unacknowledged approval and routes the client straight back here.
 *
 * The hook owns the latch as well as the request, because the two are the
 * same fact. Acknowledging is what clears `approvalAcknowledgedAt`, so the
 * caller's "first approved visit" input flips to false mid-celebration; a
 * consumer that re-derived its own celebrating flag from that input would
 * unmount the card before its Continue could fire. `celebrating` stays true
 * for the application this hook fired on until that application changes.
 *
 * Pass `applicationUuid` whenever the application is approved, and
 * `firstVisit` for the visit that should celebrate. A later visit to an
 * already-acknowledged approval passes `firstVisit: false` and gets
 * `celebrating: false` with no request.
 */
export function useApprovalAcknowledgement({
    applicationUuid,
    firstVisit,
    allowConsoleNavigation = true
}: {
    applicationUuid: string | null;
    firstVisit: boolean;
    /** Completion acknowledgement is still recorded for restricted clients,
     * but it must never become a navigation bypass. */
    allowConsoleNavigation?: boolean;
}): {
    celebrating: boolean;
    phase: ApprovalAcknowledgementPhase;
    requestContinue: () => void;
    retry: () => Promise<void>;
} {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [phase, setPhase] = useState<ApprovalAcknowledgementPhase>(firstVisit ? "pending" : "acked");
    const [wantsContinue, setWantsContinue] = useState(false);
    const [celebratingUuid, setCelebratingUuid] = useState<string | null>(null);
    // One request per application even under StrictMode. A retry calls
    // `runAck` directly and deliberately does not clear this marker.
    const firedForRef = useRef<string | null>(null);

    const runAck = useCallback(async (): Promise<void> => {
        if (!applicationUuid) return;
        setPhase("pending");
        try {
            const { bundle } = await acknowledgeApproval(applicationUuid);
            // Patch the exact query the gate reads so its next evaluation, on
            // the /positions redirect, sees an acknowledged approval; then
            // reconcile in the background. Both the gate and OnboardingEntry
            // read this key per context, which is why the patch goes through
            // the prefix-matching helper rather than a bare setQueryData.
            patchClientOnboardingApplicationsBundle(queryClient, bundle.application.uuid, () => bundle);
            void queryClient.invalidateQueries({
                queryKey: ["client-onboarding-applications"]
            });
            void queryClient.invalidateQueries({
                queryKey: [CLIENT_OPERATING_CONTEXTS_QUERY_KEY]
            });
            setPhase("acked");
        } catch {
            setPhase("error");
        }
    }, [applicationUuid, queryClient]);

    useEffect(() => {
        if (!applicationUuid) {
            // Left the approved view entirely: drop the latch so a later
            // application can celebrate on its own terms.
            setCelebratingUuid(null);
            return;
        }
        if (firedForRef.current === applicationUuid) return;
        // `firstVisit` going false while latched is the acknowledgement this
        // hook just recorded, not a reason to stop.
        if (!firstVisit) return;
        firedForRef.current = applicationUuid;
        setCelebratingUuid(applicationUuid);
        setWantsContinue(false);
        setPhase("pending");
        void runAck();
    }, [applicationUuid, firstVisit, runAck]);

    // Both the timer and the manual Continue only express intent. Navigation
    // is released once the acknowledgement has patched the gate's cache.
    useEffect(() => {
        if (allowConsoleNavigation && phase === "acked" && wantsContinue) {
            navigate("/positions");
        }
    }, [allowConsoleNavigation, navigate, phase, wantsContinue]);

    const requestContinue = useCallback((): void => {
        if (!allowConsoleNavigation) return;
        setWantsContinue(true);
    }, [allowConsoleNavigation]);

    return {
        celebrating: applicationUuid !== null && celebratingUuid === applicationUuid,
        phase,
        requestContinue,
        retry: runAck
    };
}
